import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Spinner,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
    FormGroup,
    Label
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import TableContainer from "../../Components/Common/TableContainer";
import { useNavigate } from "react-router-dom";
import { Toast, ToastBody, ToastHeader } from "reactstrap";
import { useLocation } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";

interface Invoice {
    id: string;
    invoice_ref: string;
    ref_client: string | null;
    invoice_date: string;
    date_due: string;
    total: number;
    tva: number;
    total_ttc: number;
    status: number;
    paid: number;
    type: number;
    note_public: string | null;
    date_creation: string;
    company: {
        id: string;
        name: string;
        name_alias: string;
        email: string | null;
        town: string | null;
        zip: string | null;
        code_client: string | null;
    };
    commandes: Array<{
        id: string;
        ref: string;
    }>;
}

interface InvoiceDetails {
    id: string;
    ref: string;
    datef: string;
    total_ht: number;
    total_ttc: number;
    total_tva: number;
    status: number;
    paid: number;
    type: number;
    note_public: string | null;
    company: {
        id: string;
        name: string;
        address: string | null;
        town: string | null;
        zip: string | null;
        email: string | null;
        phone: string | null;
    };
    lines: Array<{
        id: string;
        product_ref: string;
        product_label: string;
        qty: number;
        price: number;
        total_ht: number;
        tva_tx: number;
        total_tva: number;
        total_ttc: number;
    }>;
    payments?: Array<{
        id: string;
        amount: number;
        datep: string;
        type: string;
    }>;
    invoicepdf?: string;
}

interface SmartPaginationProps {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    countPerPage: number;
    onPageChange: (page: number) => void;
    loading: boolean;
}

const SmartPagination: React.FC<SmartPaginationProps> = ({
    currentPage,
    totalPages,
    totalCount,
    countPerPage,
    onPageChange,
    loading
}) => {
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    const [pageInput, setPageInput] = useState(currentPage.toString());

    useEffect(() => {
        setPageInput(currentPage.toString());
    }, [currentPage]);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    const handlePageJump = () => {
        const newPage = parseInt(pageInput);
        if (!isNaN(newPage)) {
            const clampedPage = Math.max(1, Math.min(totalPages, newPage));
            if (clampedPage !== currentPage) {
                onPageChange(clampedPage);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handlePageJump();
        }
    };

    return (
        <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
                Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong> • Total: <strong>{totalCount}</strong> factures
            </div>

            <Pagination className="mb-0">
                <PaginationItem disabled={loading}>
                    <PaginationLink 
                        first 
                        onClick={() => currentPage > 1 && onPageChange(1)} 
                        disabled={currentPage === 1}
                    />
                </PaginationItem>
                <PaginationItem disabled={loading}>
                    <PaginationLink 
                        previous 
                        onClick={() => currentPage > 1 && onPageChange(currentPage - 1)} 
                        disabled={currentPage === 1}
                    />
                </PaginationItem>

                {startPage > 1 && (
                    <>
                        <PaginationItem disabled={loading}>
                            <PaginationLink onClick={() => onPageChange(1)}>1</PaginationLink>
                        </PaginationItem>
                        {startPage > 2 && (
                            <PaginationItem disabled>
                                <PaginationLink>...</PaginationLink>
                            </PaginationItem>
                        )}
                    </>
                )}

                {pages.map(page => (
                    <PaginationItem active={page === currentPage} key={page} disabled={loading}>
                        <PaginationLink onClick={() => onPageChange(page)}>
                            {page}
                        </PaginationLink>
                    </PaginationItem>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && (
                            <PaginationItem disabled>
                                <PaginationLink>...</PaginationLink>
                            </PaginationItem>
                        )}
                        <PaginationItem disabled={loading}>
                            <PaginationLink onClick={() => onPageChange(totalPages)}>
                                {totalPages}
                            </PaginationLink>
                        </PaginationItem>
                    </>
                )}

                <PaginationItem disabled={loading}>
                    <PaginationLink 
                        next 
                        onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)} 
                        disabled={currentPage === totalPages}
                    />
                </PaginationItem>
                <PaginationItem disabled={loading}>
                    <PaginationLink 
                        last 
                        onClick={() => currentPage < totalPages && onPageChange(totalPages)} 
                        disabled={currentPage === totalPages}
                    />
                </PaginationItem>
            </Pagination>

            <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">Aller à:</span>
                <Input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ width: '70px' }}
                    className="text-center"
                    bsSize="sm"
                    disabled={loading}
                />
                <Button
                    color="primary"
                    size="sm"
                    onClick={handlePageJump}
                    disabled={loading || pageInput === currentPage.toString()}
                    style={{ minWidth: '60px' }}
                >
                    {loading ? <Spinner size="sm" /> : 'Aller'}
                </Button>
            </div>
        </div>
    );
};

const InvoicesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    // États pour les factures
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    
    // États pour la recherche
    const [searchRef, setSearchRef] = useState("");
    const [searchRefCommande, setSearchRefCommande] = useState("");
    const [searchCompany, setSearchCompany] = useState("");
    const [dateRange, setDateRange] = useState<Date[]>([]);
    const [searchType, setSearchType] = useState("");
    const [searchPaidStatus, setSearchPaidStatus] = useState<string>(
        location.state?.show_paid_status !== undefined 
            ? location.state.show_paid_status.toString() 
            : ""
    );
    
    // États pour la pagination
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [countPerPage, setCountPerPage] = useState(0);
    
    // États pour le modal de détails
    const [modalOpen, setModalOpen] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    
    // États pour les toasts
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastColor, setToastColor] = useState('info');
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
    
    // États pour le PDF
    const [invoicePdf, setInvoicePdf] = useState<string | null>(null);
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    
    // Référence pour Flatpickr
    const flatpickrRef = useRef<any>(null);
    
    // État pour suivre quelle facture est en cours de chargement
    const [loadingInvoiceId, setLoadingInvoiceId] = useState<string | null>(null);
    
    // Types de factures
    const invoiceTypes = [
        { value: "", label: "Tous les types" },
        { value: "1", label: "Avoir" }
    ];

    // Fonction pour obtenir les libellés des statuts de paiement en fonction du type
    const getPaidStatuses = () => {
        const isCreditNote = searchType === "1";
        return [
            { value: "", label: "Tous les statuts" },
            { value: "0", label: isCreditNote ? "Non consommée" : "Non payée" },
            { value: "1", label: isCreditNote ? "Consommée" : "Payée" }
        ];
    };

    
    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Fonction pour récupérer les factures
    const fetchInvoices = async () => {
        setLoading(true);
        
        try {
            const params: any = {
                action: 'invoice',
                entity: user.entity,
                page: page,
                limit: 100,
                
            };

            // Ajouter les paramètres de recherche
            if (searchRef) {
                params.search_ref = searchRef;
            }
            if (searchRefCommande) {
                params.search_ref_commande = searchRefCommande;
            }
            if (searchCompany) {
                params.search_company = searchCompany;
            }
            
            // Ajouter les paramètres de date
            if (dateRange.length === 2) {
                const startDate = dateRange[0];
                const endDate = dateRange[1];
                
                params.search_date_start = formatDate(startDate);
                params.search_date_end = formatDate(endDate);
            }
            
            // Type de facture
            if (searchType !== "") {
                params.show_type = parseInt(searchType);
            }
            
            // Statut de paiement
            if (searchPaidStatus !== "") {
                params.show_paid_status = parseInt(searchPaidStatus);
            }

            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            setInvoices(response.data || []);
            setTotalCount(response.nbtotalofrecords || 0);
            setTotalPages(response.totalpages || 1);
            setCountPerPage(response.count || 0);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setToastMessage('Erreur lors du chargement des factures');
            setToastColor('danger');
            setToastOpen(true);
        } finally {
            setLoading(false);
        }
    };

    // Fonction pour récupérer les détails d'une facture
    const fetchInvoiceDetails = async (invoiceId: string) => {
        setLoadingInvoiceId(invoiceId);
        setDetailsLoading(true);
        
        try {
            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'invoicedetail',
                    entity: user.entity,
                    id: invoiceId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data) {
                const apiData = response.data;
                
                const invoiceDetails: InvoiceDetails = {
                    id: apiData.id,
                    ref: apiData.ref,
                    datef: new Date(apiData.date * 1000).toISOString(),
                    total_ht: parseFloat(apiData.total_ht),
                    total_ttc: parseFloat(apiData.total_ttc),
                    total_tva: parseFloat(apiData.total_tva),
                    status: parseInt(apiData.status),
                    paid: apiData.paid_percent >= 100 ? 1 : 0,
                    type: parseInt(apiData.type),
                    note_public: apiData.note_public,
                    company: {
                        id: response.client?.id || '',
                        name: response.client?.name || '',
                        address: response.client?.address || null,
                        town: response.client?.town || null,
                        zip: response.client?.zip || null,
                        email: response.client?.email || null,
                        phone: response.client?.phone || null,
                    },
                    lines: apiData.lines.map((line: any) => ({
                        id: line.id,
                        product_ref: line.product_ref,
                        product_label: line.product_label || line.desc,
                        qty: parseFloat(line.qty),
                        price: parseFloat(line.subprice),
                        total_ht: parseFloat(line.total_ht),
                        tva_tx: parseFloat(line.tva_tx),
                        total_tva: parseFloat(line.total_tva),
                        total_ttc: parseFloat(line.total_ttc),
                    })),
                    payments: apiData.payments?.map((payment: any) => ({
                        id: payment.paymentid,
                        amount: parseFloat(payment.amount),
                        datep: new Date(payment.date * 1000).toISOString(),
                        type: payment.fk_typepayment || '',
                    })) || [],
                };

                setInvoiceDetails(invoiceDetails);
                
                if (response.invoicepdf) {
                    setInvoicePdf(response.invoicepdf);
                }
            }
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            setToastMessage('Erreur lors du chargement des détails');
            setToastColor('danger');
            setToastOpen(true);
        } finally {
            setDetailsLoading(false);
            setLoadingInvoiceId(null);
        }
    };

    // Fonction pour obtenir le badge de statut
    const getStatusBadge = (status: number, type: number) => {
        if (type === 2) {
            return { label: "Avoir", color: "warning" };
        }
        
        switch (status) {
            case 0: return { label: "Brouillon", color: "secondary" };
            case 1: return { label: "Validée", color: "success" };
            case 2: return { label: "Payée", color: "primary" };
            default: return { label: "Inconnu", color: "dark" };
        }
    };

    // Fonction pour obtenir le badge de paiement
    const getPaymentBadge = (paid: number) => {
        const isCreditNote = searchType === "1";
        
        switch (paid) {
            case 0: return { 
                label: isCreditNote ? "Non consommée" : "Non payée", 
                color: "danger" 
            };
            case 1: return { 
                label: isCreditNote ? "Consommée" : "Payée", 
                color: "success" 
            };
            case 2: return { 
                label: isCreditNote ? "Consommée" : "Payée", 
                color: "success" 
            };
            default: return { label: "Inconnu", color: "dark" };
        }
    };

    // Fonction pour visualiser une facture
    const handleViewInvoice = async (invoiceId: string) => {
        await fetchInvoiceDetails(invoiceId);
        setModalOpen(true);
    };

    // Fonction pour visualiser le PDF
    const handleViewPdf = () => {
        if (invoicePdf) {
            setPdfModalOpen(true);
        }
    };

    // Fonction de recherche
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        fetchInvoices();
    };

    // Fonction pour changer de page
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
            setPage(newPage);
        }
    };

    // Fonction pour réinitialiser TOUS les filtres
    const clearAllFilters = () => {
        
        setSearchRef("");
        setSearchRefCommande("");
        setSearchCompany("");
        setDateRange([]);
        setSearchType("");
        setSearchPaidStatus("");
        
       
        setPage(1);
        
  
        if (flatpickrRef.current && flatpickrRef.current.flatpickr) {
            flatpickrRef.current.flatpickr.clear();
        }
    };

    // Gestionnaires simples pour les champs de recherche
    const handleSearchRefChange = (value: string) => {
        setSearchRef(value);
    };

    const handleSearchRefCommandeChange = (value: string) => {
        setSearchRefCommande(value);
    };

    const handleSearchCompanyChange = (value: string) => {
        setSearchCompany(value);
    };

    const handleDateRangeChange = (dates: Date[]) => {
        setDateRange(dates);
    };

    const handleSearchTypeChange = (value: string) => {
        setSearchType(value);
        setSearchPaidStatus("");
        setPage(1);
    };

    const handleSearchPaidStatusChange = (value: string) => {
        setSearchPaidStatus(value);
    };

    // Colonnes pour le tableau
    const columns = [
        {
            header: "Réf. Facture",
            accessorKey: "invoice_ref",
            cell: (cell: any) => (
                <span className="fw-semibold">{cell.getValue()}</span>
            )
        },
        {
            header: "Réf. Commande",
            accessorKey: "commandes",
            cell: (cell: any) => {
                const commandes = cell.getValue();
                if (commandes && commandes.length > 0) {
                    return <span className="text-muted">{commandes[0].ref}</span>;
                }
                return <span className="text-muted">-</span>;
            }
        },
        {
            header: "Date Facture",
            accessorKey: "invoice_date",
            cell: (cell: any) => (
                <span>{new Date(cell.getValue()).toLocaleDateString()}</span>
            )
        },
        {
            header: "Client",
            accessorKey: "company.name",
            cell: (cell: any) => (
                <span>{cell.getValue()}</span>
            )
        },
        {
            header: "Total TTC",
            accessorKey: "total_ttc",
            cell: (cell: any) => (
                <span>{cell.getValue().toFixed(2)} €</span>
            )
        },
        {
            header: "Statut",
            accessorKey: "status",
            cell: (cell: any) => {
                const invoiceRow = cell.row.original;
                const status = getStatusBadge(invoiceRow.status, invoiceRow.type);
                return <Badge color={status.color}>{status.label}</Badge>;
            }
        },
        {
            header: searchType === "1" ? "Consommation" : "Paiement",
            accessorKey: "paid",
            cell: (cell: any) => {
                const payment = getPaymentBadge(cell.getValue());
                return <Badge color={payment.color}>{payment.label}</Badge>;
            }
        },
        {
            header: "Action",
            accessorKey: "id",
            cell: (cell: any) => {
                const invoiceRow = cell.row.original;
                const isThisInvoiceLoading = loadingInvoiceId === invoiceRow.id;
                
                return (
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleViewInvoice(invoiceRow.id)}
                            disabled={loading || isThisInvoiceLoading || detailsLoading}
                            className="px-2"
                        >
                            {isThisInvoiceLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Voir
                                </>
                            ) : (
                                <>
                                    <i className="ri-eye-line me-1"></i> Voir
                                </>
                            )}
                        </Button>
                    </div>
                );
            },
            meta: {
                headerClass: "text-center",
                cellClass: "text-center",
                style: {
                    textAlign: "center"
                }
            }
        }
    ];

    
    useEffect(() => {
        fetchInvoices();
    }, [page, searchRef, searchRefCommande, searchCompany, dateRange, searchType, searchPaidStatus]);

    
    useEffect(() => {
        if (location.state?.openInvoiceModal && location.state?.invoiceId) {
            handleViewInvoice(location.state.invoiceId);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    // Récupérer les paramètres du state à l'arrivée sur la page
     useEffect(() => {
        
        if (location.state?.show_paid_status !== undefined) {
           
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between mb-3">
                                    <h4 className="card-title mb-0">Liste des Factures</h4>
                                    <div className="text-muted">
                                        Total: <strong>{totalCount}</strong> factures
                                    </div>
                                </div>

                                <form onSubmit={handleSearch} className="mb-4">
                                    <Row className="g-3 align-items-end">
                                        {/* Première ligne de filtres */}
                                        <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1">
                                                    RÉF. FACTURE
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText 
                                                        className="bg-light border-end-0"
                                                        style={{ cursor: "default", pointerEvents: "none" }}
                                                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#e9ecef";
                                                            e.currentTarget.style.color = "#495057";
                                                        }}
                                                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                            e.currentTarget.style.color = "";
                                                        }}
                                                    >
                                                        <i className="ri-barcode-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        placeholder="Référence..."
                                                        value={searchRef}
                                                        onChange={(e) => handleSearchRefChange(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1">
                                                    RÉF. COMMANDE
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText 
                                                        className="bg-light border-end-0"
                                                        style={{ cursor: "default", pointerEvents: "none" }}
                                                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#e9ecef";
                                                            e.currentTarget.style.color = "#495057";
                                                        }}
                                                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                            e.currentTarget.style.color = "";
                                                        }}
                                                    >
                                                        <i className="ri-file-text-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        placeholder="Référence..."
                                                        value={searchRefCommande}
                                                        onChange={(e) => handleSearchRefCommandeChange(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1">
                                                    PÉRIODE
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText 
                                                        className="bg-light border-end-0"
                                                        style={{ cursor: "default", pointerEvents: "none" }}
                                                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#e9ecef";
                                                            e.currentTarget.style.color = "#495057";
                                                        }}
                                                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                            e.currentTarget.style.color = "";
                                                        }}
                                                    >
                                                        <i className="ri-calendar-line"></i>
                                                    </InputGroupText>
                                                    <Flatpickr
                                                        className="form-control border-start-0"
                                                        options={{
                                                            mode: "range",
                                                            dateFormat: "d/m/Y",
                                                            allowInput: true,
                                                            locale: {
                                                                firstDayOfWeek: 1,
                                                                weekdays: {
                                                                    shorthand: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
                                                                    longhand: [
                                                                        "Dimanche", "Lundi", "Mardi", "Mercredi", 
                                                                        "Jeudi", "Vendredi", "Samedi"
                                                                    ]
                                                                },
                                                                months: {
                                                                    shorthand: [
                                                                        "Janv", "Févr", "Mars", "Avr", "Mai", "Juin",
                                                                        "Juil", "Août", "Sept", "Oct", "Nov", "Déc"
                                                                    ],
                                                                    longhand: [
                                                                        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                                                                        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
                                                                    ]
                                                                }
                                                            },
                                                            onReady: (selectedDates, dateStr, instance) => {
                                                                instance.set("mode", "range");
                                                            }
                                                        }}
                                                        onChange={(dates) => {
                                                            handleDateRangeChange(dates);
                                                        }}
                                                        style={{ height: "42px" }}
                                                        placeholder="Sélectionner une période"
                                                        value={dateRange}
                                                        ref={flatpickrRef}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1">
                                                    TYPE
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText 
                                                        className="bg-light border-end-0"
                                                        style={{ cursor: "default", pointerEvents: "none" }}
                                                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#e9ecef";
                                                            e.currentTarget.style.color = "#495057";
                                                        }}
                                                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                            e.currentTarget.style.color = "";
                                                        }}
                                                    >
                                                        <i className="ri-file-text-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="select"
                                                        value={searchType}
                                                        onChange={(e) => handleSearchTypeChange(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    >
                                                        {invoiceTypes.map((type) => (
                                                            <option key={type.value} value={type.value}>
                                                                {type.label}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1">
                                                    {searchType === "1" ? "CONSOMMATION" : "PAIEMENT"}
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText 
                                                        className="bg-light border-end-0"
                                                        style={{ cursor: "default", pointerEvents: "none" }}
                                                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#e9ecef";
                                                            e.currentTarget.style.color = "#495057";
                                                        }}
                                                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                            e.currentTarget.style.color = "";
                                                        }}
                                                    >
                                                        <i className="ri-money-euro-circle-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="select"
                                                        value={searchPaidStatus}
                                                        onChange={(e) => handleSearchPaidStatusChange(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    >
                                                        {getPaidStatuses().map((status) => (
                                                            <option key={status.value} value={status.value}>
                                                                {status.label}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        
                                        <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1">
                                                    CLIENT
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText 
                                                        className="bg-light border-end-0"
                                                        style={{ cursor: "default", pointerEvents: "none" }}
                                                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#e9ecef";
                                                            e.currentTarget.style.color = "#495057";
                                                        }}
                                                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                            e.currentTarget.style.color = "";
                                                        }}
                                                    >
                                                        <i className="ri-user-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        placeholder="Nom client..."
                                                        value={searchCompany}
                                                        onChange={(e) => handleSearchCompanyChange(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                       
                                        <Col md={10}></Col>
                                        
                                        {/* Bouton Rechercher et icône effacer les filtres */}
                                        <Col md={2} className="d-flex align-items-end">
                                            <FormGroup className="mb-0 w-100">
                                                <Label className="form-label fw-semibold small text-muted mb-1 d-block invisible">
                                                    &nbsp;
                                                </Label>
                                                <div className="d-flex gap-2" style={{ height: "42px" }}>
                                                    <Button
                                                        type="submit"
                                                        color="primary"
                                                        className="flex-grow-1"
                                                        style={{ 
                                                            height: "100%", 
                                                            width: "calc(100% - 58px)",
                                                            maxWidth: "200px"
                                                        }}
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <Spinner size="sm" />
                                                        ) : (
                                                            <>
                                                                <i className="ri-search-line align-bottom me-1"></i>
                                                                Rechercher
                                                            </>
                                                        )}
                                                    </Button>

                                                    <Button
                                                        color="light"
                                                        onClick={clearAllFilters}
                                                        disabled={loading}
                                                        className="d-flex align-items-center justify-content-center border"
                                                        style={{ 
                                                            width: "42px",
                                                            padding: "0",
                                                            flexShrink: 0
                                                        }}
                                                        title="Effacer tous les filtres"
                                                    >
                                                        <i className="ri-filter-off-line"></i>
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </form>

                                {/* Filtres actifs */}
                                <div className="mb-3">
                                    {(searchRef || searchRefCommande || searchCompany || dateRange.length > 0 || searchType || searchPaidStatus !== "") && (
                                        <div className="alert alert-info py-2">
                                            <small>
                                                <i className="ri-filter-line me-1"></i>
                                                Filtres actifs: 
                                                {searchRef && (
                                                    <Badge color="info" className="ms-2">
                                                        Réf: {searchRef}
                                                    </Badge>
                                                )}
                                                {searchRefCommande && (
                                                    <Badge color="info" className="ms-2">
                                                        Cde: {searchRefCommande}
                                                    </Badge>
                                                )}
                                                {searchCompany && (
                                                    <Badge color="info" className="ms-2">
                                                        Client: {searchCompany}
                                                    </Badge>
                                                )}
                                                {searchType && searchType !== "" && (
                                                    <Badge color="info" className="ms-2">
                                                        Type: {invoiceTypes.find(t => t.value === searchType)?.label}
                                                    </Badge>
                                                )}
                                                {searchPaidStatus !== "" && (
                                                    <Badge color="info" className="ms-2">
                                                        {searchType === "1" ? "Consommation" : "Paiement"}: {getPaidStatuses().find(p => p.value === searchPaidStatus)?.label}
                                                    </Badge>
                                                )}
                                                {dateRange.length === 2 && (
                                                    <Badge color="info" className="ms-2">
                                                        Période: {
                                                            dateRange[0].toLocaleDateString() === dateRange[1].toLocaleDateString()
                                                                ? dateRange[0].toLocaleDateString()
                                                                : `${dateRange[0].toLocaleDateString()} au ${dateRange[1].toLocaleDateString()}`
                                                        }
                                                    </Badge>
                                                )}
                                            </small>
                                        </div>
                                    )}
                                </div>

                                {loading && invoices.length === 0 ? (
                                    <div className="text-center py-4">
                                        <Spinner color="primary" />
                                        <div className="mt-2">Chargement des factures...</div>
                                    </div>
                                ) : (
                                    <>
                                        {invoices.length === 0 ? (
                                            <div className="text-center py-4">
                                                <i className="ri-file-search-line display-5 text-muted"></i>
                                                <h5 className="mt-3">Aucune facture trouvée</h5>
                                                <p className="text-muted">
                                                    {searchRef || searchRefCommande || searchCompany || dateRange.length > 0 || searchType || searchPaidStatus !== ""
                                                        ? "Essayez avec d'autres critères de recherche"
                                                        : "Il n'y a aucune facture à afficher"}
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="position-relative">
                                                    {loading && (
                                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                                                            <div className="text-center">
                                                                <Spinner color="primary" />
                                                                <div className="mt-2">Chargement...</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <TableContainer
                                                        columns={columns}
                                                        data={invoices}
                                                        isGlobalFilter={false}
                                                        customPageSize={100}
                                                        divClass="table-responsive"
                                                        tableClass="align-middle table-nowrap"
                                                        theadClass="table-light text-muted"
                                                    />
                                                </div>

                                                {invoices.length > 0 && (
                                                    <SmartPagination
                                                        currentPage={page}
                                                        totalPages={totalPages}
                                                        totalCount={totalCount}
                                                        countPerPage={countPerPage}
                                                        onPageChange={handlePageChange}
                                                        loading={loading}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Modal pour les détails de la facture */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    <div>
                        Facture {invoiceDetails?.ref}
                        {invoiceDetails && (
                            <Badge color={getStatusBadge(invoiceDetails.status, invoiceDetails.type).color} className="ms-2">
                                {getStatusBadge(invoiceDetails.status, invoiceDetails.type).label}
                            </Badge>
                        )}
                    </div>
                </ModalHeader>
                <ModalBody>
                    {detailsLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <div className="mt-2">Chargement des détails...</div>
                        </div>
                    ) : invoiceDetails ? (
                        <div className="invoice-details-container">
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="p-3 border rounded mb-3">
                                        <h6>Informations client</h6>
                                        <div className="mb-2">
                                            <strong>Client:</strong> {invoiceDetails.company.name || "Non spécifié"}
                                        </div>
                                        {invoiceDetails.company.address && (
                                            <div className="mb-2">
                                                <strong>Adresse:</strong> {invoiceDetails.company.address}
                                            </div>
                                        )}
                                        {invoiceDetails.company.email && (
                                            <div className="mb-2">
                                                <strong>Email:</strong> {invoiceDetails.company.email}
                                            </div>
                                        )}
                                        {invoiceDetails.company.phone && (
                                            <div className="mb-2">
                                                <strong>Téléphone:</strong> {invoiceDetails.company.phone}
                                            </div>
                                        )}
                                        <div>
                                            <strong>Date facture:</strong> {new Date(invoiceDetails.datef).toLocaleDateString()}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 border rounded mb-3">
                                        <h6>Résumé financier</h6>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Total HT:</span>
                                            <span>{invoiceDetails.total_ht.toFixed(2)} €</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>TVA:</span>
                                            <span>{invoiceDetails.total_tva.toFixed(2)} €</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Total TTC:</span>
                                            <span>{invoiceDetails.total_ttc.toFixed(2)} €</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Statut paiement:</span>
                                            <Badge color={getPaymentBadge(invoiceDetails.paid).color}>
                                                {getPaymentBadge(invoiceDetails.paid).label}
                                            </Badge>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Lignes de la facture */}
                            {invoiceDetails.lines && invoiceDetails.lines.length > 0 && (
                                <div className="table-responsive">
                                    <h6>Articles</h6>
                                    <Table striped bordered hover className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Référence</th>
                                                <th>Description</th>
                                                <th className="text-end">Quantité</th>
                                                <th className="text-end">Prix unitaire</th>
                                                <th className="text-end">Total HT</th>
                                                <th className="text-end">Total TTC</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceDetails.lines.map((line, index) => (
                                                <tr key={index}>
                                                    <td>{line.product_ref}</td>
                                                    <td style={{ maxWidth: '400px' }}>{line.product_label}</td>
                                                    <td className="text-end">{line.qty}</td>
                                                    <td className="text-end">{line.price.toFixed(2)} €</td>
                                                    <td className="text-end">{line.total_ht.toFixed(2)} €</td>
                                                    <td className="text-end">{line.total_ttc.toFixed(2)} €</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {/* Paiements */}
                            {invoiceDetails.payments && invoiceDetails.payments.length > 0 && (
                                <div className="mt-4">
                                    <h6>Paiements</h6>
                                    <Table striped bordered hover className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th className="text-end">Montant</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceDetails.payments.map((payment, index) => (
                                                <tr key={index}>
                                                    <td>{new Date(payment.datep).toLocaleDateString()}</td>
                                                    <td>{payment.type}</td>
                                                    <td className="text-end">{payment.amount.toFixed(2)} €</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-danger"></i>
                            <h5 className="mt-3">Détails non disponibles</h5>
                        </div>
                    )}
                </ModalBody>
                
                <ModalFooter>
                    {invoicePdf && (
                        <Button
                            color="info"
                            size="md"
                            className="me-2"
                            onClick={handleViewPdf}
                        >
                            <i className="ri-file-text-line align-bottom me-2"></i> Voir PDF
                        </Button>
                    )}
                    <Button
                        color="secondary"
                        size="md"
                        onClick={() => setModalOpen(false)}
                    >
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Modal pour le PDF */}
            <Modal 
                isOpen={pdfModalOpen} 
                toggle={() => setPdfModalOpen(false)}
                size="xl" 
                style={{ maxWidth: '90vw' }}
            >
                <ModalHeader toggle={() => setPdfModalOpen(false)}>
                    <div>
                        <i className="ri-file-pdf-line me-2 text-danger"></i>
                        Facture {invoiceDetails?.ref}
                    </div>
                </ModalHeader>
                <ModalBody style={{ height: '80vh', padding: '0' }}>
                    {invoicePdf ? (
                        <iframe
                            src={invoicePdf}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title={`Facture ${invoiceDetails?.ref}`}
                        />
                    ) : (
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-muted"></i>
                            <h5 className="mt-3">PDF non disponible</h5>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* Toast pour les notifications */}
            {toastOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{ width: '400px', maxWidth: '90vw' }}>
                        <Toast isOpen={toastOpen} className={`bg-white text-dark`}>
                            <ToastHeader
                                toggle={() => {
                                    setToastOpen(false);
                                    setConfirmAction(null);
                                }}
                                className={`bg-white text-dark d-flex justify-content-between align-items-center`}
                            >
                                <span>
                                    <i className={`me-2 ${toastColor === 'warning' ? 'ri-information-line' : toastColor === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
                                    {toastColor === 'warning' ? 'Confirmation' : toastColor === 'success' ? 'Succès' : 'Erreur'}
                                </span>
                            </ToastHeader>
                            <ToastBody>
                                <div className="d-flex flex-column">
                                    <p className="mb-3 text-center">{toastMessage}</p>
                                    {toastColor === 'warning' && confirmAction && (
                                        <div className="d-flex gap-2 justify-content-center">
                                            <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => {
                                                    setToastOpen(false);
                                                    setConfirmAction(null);
                                                }}
                                                className="flex-fill"
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => {
                                                    if (confirmAction) {
                                                        confirmAction();
                                                    }
                                                    setToastOpen(false);
                                                    setConfirmAction(null);
                                                }}
                                                className="flex-fill"
                                            >
                                                Confirmer
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </ToastBody>
                        </Toast>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoicesPage;